/**
 * blogApi — typed client for the migrated 165-post blog surface.
 *
 * Mirrors api/client.ts; reuses the shared `api` fetch wrapper so JWT/tenant
 * plumbing stays in one place. Endpoints:
 *   GET /api/blog/posts         → { posts, total }
 *   GET /api/blog/posts/{slug}  → single post + Yoast metadata
 */

import { api } from './client'

/** One of the 4 real categories the 165 posts are retagged under. */
export type BlogCategory = 'home-loans' | 'refinancing' | 'business' | 'market-updates'

/** Summary shape returned in the index listing. */
export interface BlogPostSummary {
  slug: string
  title: string
  category: string
  excerpt?: string
  published_at?: string
}

/** Wrapper returned by GET /api/blog/posts. */
export interface BlogPostListResponse {
  posts: BlogPostSummary[]
  total: number
}

/** Full post returned by GET /api/blog/posts/{slug}, Yoast metadata preserved. */
export interface BlogPost {
  slug: string
  title: string
  /** Article HTML. NOTE: the API returns this as `body` (not `content`). */
  body: string
  excerpt?: string
  category?: string
  published_at?: string
  yoast_title?: string
  yoast_meta_description?: string
}

/** Admin post shape (authoring side — includes id + status, lists drafts too). */
export interface BlogAdminPost {
  id: string
  slug: string
  title: string
  body: string
  excerpt?: string | null
  category?: string | null
  status: 'draft' | 'publish'
  yoast_title?: string | null
  yoast_meta_description?: string | null
  published_at?: string | null
  created_at?: string | null
  updated_at?: string | null
}

/** Fields accepted when creating a post. */
export interface BlogPostInput {
  title: string
  body: string
  category: string
  status: 'draft' | 'publish'
  excerpt?: string
  yoast_title?: string
  yoast_meta_description?: string
}

/** All fields optional on update (dynamic patch). */
export type BlogPostPatch = Partial<BlogPostInput>

export const blogApi = {
  listPosts: () => api.get<BlogPostListResponse>('/api/blog/posts'),
  getPost: (slug: string) => api.get<BlogPost>(`/api/blog/posts/${encodeURIComponent(slug)}`),

  // Authoring (require_admin on the server).
  listAdminPosts: () => api.get<{ posts: BlogAdminPost[]; total: number }>('/api/admin/blog/posts'),
  getAdminPost: (id: string) => api.get<BlogAdminPost>(`/api/admin/blog/posts/${encodeURIComponent(id)}`),
  createPost: (input: BlogPostInput) => api.post<BlogAdminPost>('/api/admin/blog/posts', input),
  updatePost: (id: string, patch: BlogPostPatch) =>
    api.patch<BlogAdminPost>(`/api/admin/blog/posts/${encodeURIComponent(id)}`, patch),
  deletePost: (id: string) => api.delete<{ deleted: string }>(`/api/admin/blog/posts/${encodeURIComponent(id)}`),
  // Trigger a full SSG rebuild + Netlify draft deploy; the reviewer is emailed the draft link.
  requestRebuild: () => api.post<{ status: string }>('/api/admin/blog/rebuild'),
}
